import type { TrackingProps } from '@seek/tealium-adapter/lib-types/types';
import { type ReactNode, createContext, useContext } from 'react';

import type { AnalyticsClient } from 'src/App/utils/analytics/initiaseTealiumAdaptor';

import { useContractBalancesQuery } from '../generated/graphql-types';
import { useQueryAccountInfo } from '../hooks/useQueryAccountInfo.ts';
import { mapAdvertiserContracts } from '../utils/analytics/event';
import { AppError } from '../utils/error';
import { getLogger } from '../utils/logger/logger';

import { useAppContext } from './AppContext';

interface TrackingFunctionProps {
  eventName: string;
  data: TrackingProps;
}

export interface Analytics {
  trackView: (input: TrackingFunctionProps) => void;
  trackLink: (input: TrackingFunctionProps) => void;
}

const TrackingContext = createContext<Analytics>({
  trackView: () => {},
  trackLink: () => {},
});

export const TrackingProvider = ({
  analyticsClient,
  children,
}: {
  children: ReactNode;
  analyticsClient: AnalyticsClient;
}) => {
  const appContext = useAppContext();
  const { accountInfo } = useQueryAccountInfo();
  const advertiserId = accountInfo?.currentAdvertiser?.advertiserId ?? '';
  const seekAdvertiserId = accountInfo?.currentAdvertiser?.seekId ?? '';
  const userEmail = accountInfo?.identityContext?.email ?? '';
  const userFullName = accountInfo?.user?.name ?? '';
  const userId = accountInfo?.user?.id ?? '';

  const { data: contractBalancesResponse, error: contractBalancesError } =
    useContractBalancesQuery({
      skip: !advertiserId,
    });

  if (contractBalancesError) {
    const balancesError = new AppError(
      'GET_CONTRACT_BALANCES_ERROR',
      'Error retrieving contract balances',
      contractBalancesError,
    );
    getLogger().error(balancesError.message, {
      activity: 'initialiseAdBudgetsInformation',
      error: balancesError,
    });
  }

  const advertiserContracts = contractBalancesResponse?.contractBalances;
  const advertiserCommitmentType = advertiserId &&
    advertiserContracts && {
      advertiserCommitmentType: mapAdvertiserContracts(advertiserContracts),
    };

  const isLoggedIn = Boolean(advertiserId) ? 'true' : 'false';
  const globaldata = {
    brand: appContext.brand,
    siteCountry: appContext.country,
    siteLanguage: appContext.language,
    zone: appContext.zone,
    isLoggedIn,
    ...(userId && { userId }),
    ...(seekAdvertiserId && { seekAdvertiserId }),
    ...(userEmail && { hirerUserEmail: userEmail }),
    ...(userFullName && { hirerUserFullName: userFullName }),
    ...(advertiserCommitmentType && advertiserCommitmentType),
    siteSection: 'products',
    // @NOTE: Currently setting the "currentPage" based on the active URL
    // and converting it a friendlier name (ie, products ad budgets) by stripping
    // out the / and - characters. In future we might get a specific list of names
    // but for now this achieves the goal of specifying a relevant currentPage value
    currentPage: (
      (typeof window !== 'undefined' && window?.location?.pathname) ||
      '/products/ad-budgets'
    )
      .replace(/[\/-]/g, ' ')
      .toLowerCase()
      .trim(),
  };

  const trackView = (input: TrackingFunctionProps) => {
    analyticsClient.view(input.eventName, {
      ...globaldata,
      ...input.data,
      linkName: input.eventName,
    });
  };

  const trackLink = (input: TrackingFunctionProps) => {
    analyticsClient.link(input.eventName, {
      ...globaldata,
      ...input.data,
      linkName: input.eventName,
    });
  };

  return (
    <TrackingContext.Provider value={{ trackView, trackLink }}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = () => useContext(TrackingContext);
